import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';

// Pages
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import Trial from './pages/Trial/Trial';
import TrialRedirect from './pages/TrialRedirect/TrialRedirect';
import EmailConfirmation from './pages/EmailConfirmation/EmailConfirmation';
import Dashboard from './pages/Dashboard/Dashboard';
import SearchList from './pages/SearchList/SearchList';
import SearchCreation from './pages/SearchCreation/SearchCreation';
import SearchDetails from './pages/SearchDetails/SearchDetails';
import Results from './pages/Results/Results';
import Admin from './pages/Admin/Admin';
import AdminSearchDetails from './pages/Admin/AdminSearchDetails';
import AdminUserDetails from './pages/Admin/AdminUserDetails';
import Analytics from './pages/Admin/Analytics';
import Subscriptions from './pages/Subscriptions/Subscriptions';
import StartTrial from './pages/StartTrial/StartTrial';
import CheckoutReturn from './pages/StartTrial/CheckoutReturn';
import Settings from './pages/Settings/Settings';
import ComparisonToolLanding from './pages/ComparisonTool/ComparisonToolLanding';
import ComparisonForm from './pages/ComparisonTool/ComparisonForm';
import ComparisonSuccess from './pages/ComparisonTool/ComparisonSuccess';

// Auth
import { useAuth } from './hooks/useAuth';
import { TOKEN_STORAGE_KEY, storeAuthData } from './utils/auth';
import { setOnboardingCompleted, setPostSearchSurveyCompleted } from './store/slices/authSlice';
import { useAppDispatch } from './store/hooks';

// Onboarding
import { OnboardingModal } from './components/onboarding/OnboardingModal';
import { PostSearchSurveyModal } from './components/surveys/PostSearchSurveyModal';

// Hooks
import { useEmailVerificationInit } from './hooks/useEmailVerificationInit';
import { useSubscriptionGate } from './hooks/useSubscriptionGate';

// Global people enrichment polling
import DMUnlockManager from './components/dmUnlock/DMUnlockManager';
import PostHogAuthBridge from './components/analytics/PostHogAuthBridge';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode; skipSubscriptionGate?: boolean }> = ({
  children,
  skipSubscriptionGate = false,
}) => {
  const { isAuthenticated, loading, fetchCurrentUser, user, token } = useAuth();
  const dispatch = useAppDispatch();
  const authCheckedRef = useRef(false);
  const { hasSubscription, loading: gateLoading } = useSubscriptionGate(isAuthenticated && !skipSubscriptionGate);

  useEffect(() => {
    // Check for token in localStorage directly to ensure we have the latest state
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    // Only try to fetch user data if token exists and we haven't already checked
    if (token && !authCheckedRef.current) {
      console.log('ProtectedRoute: Token found, fetching current user');
      fetchCurrentUser({ force: true });
      authCheckedRef.current = true;
    } else if (!token) {
      console.log('ProtectedRoute: No token found');
      authCheckedRef.current = true;
    }
  }, [fetchCurrentUser]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('ProtectedRoute: User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (!skipSubscriptionGate && gateLoading && hasSubscription === null) {
    return (
      <div className="flex justify-center items-center h-screen">
        <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!skipSubscriptionGate && hasSubscription === false) {
    console.log('ProtectedRoute: No subscription found, redirecting to /start-trial');
    return <Navigate to="/start-trial" replace />;
  }

  console.log('ProtectedRoute: User authenticated, rendering route');
  return (
    <>
      {children}
      {user && !user.onboarding_completed && (
        <OnboardingModal
          username={user.username}
          onComplete={() => {
            dispatch(setOnboardingCompleted());
            if (token) storeAuthData(token, { ...user, onboarding_completed: true });
          }}
        />
      )}
      {user &&
        user.onboarding_completed &&
        !user.post_search_survey_completed &&
        (user.completed_searches_count ?? 0) >= 2 && (
          <PostSearchSurveyModal
            username={user.username}
            completedSearchesCount={user.completed_searches_count ?? 0}
            onComplete={() => {
              dispatch(setPostSearchSurveyCompleted());
              if (token) storeAuthData(token, { ...user, post_search_survey_completed: true });
            }}
          />
        )}
    </>
  );
};

// App Routes setup
const AppRoutes: React.FC = () => {
  const { fetchCurrentUser, token, isAuthenticated } = useAuth();
  const location = useLocation();
  const fetchTimeoutRef = useRef<number | null>(null);
  const lastPathRef = useRef<string>(location.pathname);

  // Initialize email verification state restoration
  useEmailVerificationInit();

  // This useEffect is now only responsible for handling auth on the public routes
  // and initial app load. ProtectedRoute handles auth for protected routes.
  useEffect(() => {
    // Don't fetch user data on login or register pages
    const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/trial';
    
    // Only fetch if:
    // 1. We're not on an auth page
    // 2. We have a token
    // 3. The path has changed (to prevent multiple fetches on the same page)
    // 4. We're not already authenticated
    if (!isAuthPage && token && (location.pathname !== lastPathRef.current || !isAuthenticated)) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current !== null) {
        window.clearTimeout(fetchTimeoutRef.current);
      }
      
      // Set a timeout to debounce the fetch call
      fetchTimeoutRef.current = window.setTimeout(() => {
        console.log('AppRoutes: Fetching current user');
        fetchCurrentUser();
        fetchTimeoutRef.current = null;
      }, 300); // 300ms debounce
      
      // Update the last path
      lastPathRef.current = location.pathname;
    }
    
    // Cleanup function
    return () => {
      if (fetchTimeoutRef.current !== null) {
        window.clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [location.pathname, fetchCurrentUser, token, isAuthenticated]);
  
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/trial" element={<Trial />} />
      <Route path="/c32bfa6ad2c94bdbfeee3a4293c8460a5c3538c621abe550e74cf72b7886b6ba" element={<TrialRedirect />} />
      <Route path="/confirm-email/:token" element={<EmailConfirmation />} />

      <Route path="/comparison-tool" element={<ComparisonToolLanding />} />
      <Route path="/comparison-tool/form" element={<ComparisonForm />} />
      <Route path="/comparison-tool/success" element={<ComparisonSuccess />} />

      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/searches" 
        element={
          <ProtectedRoute>
            <SearchList />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/searches/region" 
        element={<Navigate to="/searches/new" replace />} 
      />
      
      <Route 
        path="/searches/new" 
        element={
          <ProtectedRoute>
            <SearchCreation />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/searches/:id" 
        element={
          <ProtectedRoute>
            <SearchDetails />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/searches/:id/results" 
        element={
          <ProtectedRoute>
            <Results />
          </ProtectedRoute>
        } 
      />
      
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <Subscriptions />
          </ProtectedRoute>
        }
      />

      <Route
        path="/start-trial"
        element={
          <ProtectedRoute skipSubscriptionGate>
            <StartTrial />
          </ProtectedRoute>
        }
      />

      <Route
        path="/start-trial/return"
        element={
          <ProtectedRoute skipSubscriptionGate>
            <CheckoutReturn />
          </ProtectedRoute>
        }
      />
      
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/analytics" 
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        } 
      />
      
      <Route
        path="/admin/searches/:id"
        element={
          <ProtectedRoute>
            <AdminSearchDetails />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute>
            <AdminUserDetails />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router>
        <PostHogAuthBridge />
        <DMUnlockManager />
        <AppRoutes />
      </Router>
    </Provider>
  );
};

export default App; 
